import { useCallback, useEffect, useRef } from 'react';

interface UseAutoplayProps {
  isEnabled: boolean;
  interval: number;
  onNext: () => void;
  pauseOnInteraction?: boolean;
}

interface UseAutoplayReturn {
  pause: () => void;
  resume: () => void;
  isPaused: boolean;
}

export function useAutoplay({
  isEnabled,
  interval,
  onNext,
  pauseOnInteraction: _pauseOnInteraction = true
}: UseAutoplayProps): UseAutoplayReturn {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPausedRef = useRef(false);

  const clearAutoplay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAutoplay = useCallback(() => {
    if (isEnabled && !isPausedRef.current) {
      clearAutoplay();
      intervalRef.current = setInterval(onNext, interval);
    }
  }, [isEnabled, interval, onNext, clearAutoplay]);

  const pause = useCallback(() => {
    isPausedRef.current = true;
    clearAutoplay();
  }, [clearAutoplay]);

  const resume = useCallback(() => {
    isPausedRef.current = false;
    startAutoplay();
  }, [startAutoplay]);

  useEffect(() => {
    if (isEnabled) {
      startAutoplay();
    } else {
      clearAutoplay();
    }

    return clearAutoplay;
  }, [isEnabled, startAutoplay, clearAutoplay]);

  useEffect(() => {
    return () => {
      clearAutoplay();
    };
  }, [clearAutoplay]);

  return {
    pause,
    resume,
    isPaused: isPausedRef.current
  };
}
